import antdTheme from 'antd/lib/theme';
import type { ThemeConfig } from 'antd';

/** Semantic values owned by the native FormGroup primitive. */
export const formGroupTokens = {
    labelTextTransform: 'uppercase',
    labelFontWeight: 'bold',
    labelLetterSpacing: 0.2,
    disabledOpacity: 0.6,
} as const;

/**
 * Package-owned design tokens for `@ui/components`.
 *
 * Single source of truth for the library's AntD theme. Ported from the MVC
 * app brand theme (`src/constants/theme.ts`) so the package is extraction-clean
 * (no `@app/*` dependency). Converging the app onto these tokens is a separate,
 * out-of-scope ticket — until then the two are intentionally duplicated.
 */
export const tokens: ThemeConfig = {
    token: {
        colorPrimary: '#009FDF', // Relo Blue
        colorError: '#D4047D', // Relo Red
        colorWarning: '#FF7500', // Relo Orange
        colorSuccess: '#2DC850', // Relo Green
        colorTextBase: '#000000', // Relo Black
        colorBgLayout: '#E8E9EA', // Relo White Background
        colorBgContainer: '#FFFFFF', // Relo White Container
        // Primary hover, focus, and pressed states
        colorPrimaryHover: '#529EBD',
        colorPrimaryActive: '#045778',
        // Success hover state
        colorSuccessHover: 'rgba(45, 200, 80, 0.3)',
        // Warning hover and pressed states
        colorWarningHover: '#CB8504',
        colorWarningActive: '#9C561A',
        // Error hover and focus states
        colorErrorHover: '#E66FB1',
        colorErrorActive: '#B6066A',
        colorText: '#515252',
        fontSize: 14,
        fontFamily: 'Roboto, sans-serif',
        colorBorderSecondary: '#BFBFBF',
    },
    algorithm: antdTheme.defaultAlgorithm,
    components: {
        Layout: {
            lightSiderBg: '#E8E9EA',
        },
        Menu: {
            itemSelectedBg: '#d3dce0',
            itemActiveBg: '#A3BDC7',
            itemSelectedColor: '#0480B2',
            popupBg: '#E8E9EA',
            colorText: '#515252',
            fontSize: 14,
            groupTitleFontSize: 14,
        },
        Collapse: {
            headerBg: '#FAFAFA',
            colorBorder: '#BFBFBF',
            colorBgContainerDisabled: '#E8E9EA',
        },
        Tag: {
            colorBgBase: 'E6F4FF',
        },
        Select: {
            optionSelectedBg: '#E6F4FF',
        },
        DatePicker: {
            cellActiveWithRangeBg: '#E6F4FF',
        },
        Tabs: {
            itemHoverColor: '#009FDF',
            fontSize: 16,
        },
        Table: {
            headerBg: '#A3BDC7',
            headerColor: '#045778',
            footerBg: '#A3BDC7',
            footerColor: '#045778',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            selectionColumnWidth: 33,
            rowSelectedBg: '#a4bdc7',
            rowSelectedHoverBg: '#d3dce0',
        },
        Button: {
            colorText: '#FFFFFF',
        },
        Form: {
            // Field label reads like the legacy gds form-group label: small and
            // secondary-toned. Uppercase/bold is applied by FormGroupLabel.
            labelColor: '#7a7a7a',
            labelFontSize: 12,
            labelRequiredMarkColor: '#D4047D', // Relo Red
            verticalLabelPadding: '0 0 4px',
        },
    },
};

export default tokens;
